import { useMemo } from 'react';
import { STANDARD_DATE_FORMAT } from '@/configs';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';

type SocietySettingLike = {
  societySettingType?: string | null;
  value?: string | null;
};

type SocietyDisplayOptionsJson = {
  dateFormat?: string | null;
};

function parseSocietyDisplayOptions(
  settings: SocietySettingLike[] | undefined,
): SocietyDisplayOptionsJson | null {
  if (!settings?.length) return null;

  const setting = settings.find(
    (s) => s?.societySettingType === SOCIETY_SETTING_TYPE.DISPLAY_OPTIONS,
  );
  if (!setting?.value) return null;

  try {
    return JSON.parse(setting.value) as SocietyDisplayOptionsJson;
  } catch {
    return null;
  }
}

/**
 * Society-wide display profile from setting type 30 (`DISPLAY_OPTIONS`).
 * Currently exposes `dateFormat` (date-fns pattern) for typed date fields.
 */
function useSocietyDisplayOptions(): {
  dateFormat: string;
} {
  const settings = usePublicSearchContext((c) => c.society.settings);

  return useMemo(() => {
    const raw = parseSocietyDisplayOptions(settings);
    const dateFormat =
      typeof raw?.dateFormat === 'string' && raw.dateFormat.trim()
        ? raw.dateFormat.trim()
        : STANDARD_DATE_FORMAT;
    return { dateFormat };
  }, [settings]);
}

/** Society date-fns format, defaulting to {@link STANDARD_DATE_FORMAT}. */
export function useSocietyDateFormat(): string {
  return useSocietyDisplayOptions().dateFormat;
}
